import {type DocumentNode, gql} from '@apollo/client';
import {
    type Filter,
    type GqlDownloadRequestPayload,
    type GqlRequest,
    GqlRequestBuilder,
    type IDownloadByLinkRequestBuilder,
    maxServerDownloadPageSize,
} from '@igp/shared/api';
import {
    type AgentPlayerReportingFilter,
    type AgentPlayerReportingV2,
    type QueryExportAgentPlayerReportingsV2Args,
    type QueryGetAgentPlayerReportingsV2Args,
    type Sorting,
    SortOrder,
} from '@igp/shared/contract/pam';
import type {EntityType} from '@models';

import type {
    PlayerLevelReportV2DownloadFields,
    PlayerLevelReportV2QueryFilterKeys,
    PlayerLevelReportV2QueryKeys,
    PlayerLevelReportV2SortKeys,
} from './types';

type PlayerLevelReportV2ServerTextFilterKeys = Extract<PlayerLevelReportV2QueryFilterKeys, 'uid_un' | 'uid' | 'username' | 'country'>;

export class PlayerLevelReportV2RequestBuilder
    extends GqlRequestBuilder<
        EntityType,
        QueryGetAgentPlayerReportingsV2Args,
        PlayerLevelReportV2QueryKeys,
        PlayerLevelReportV2QueryFilterKeys
    >
    implements IDownloadByLinkRequestBuilder<QueryExportAgentPlayerReportingsV2Args, PlayerLevelReportV2DownloadFields>
{
    buildDownloadRequest(
        downloadRequestPayload: GqlDownloadRequestPayload<PlayerLevelReportV2DownloadFields>
    ): GqlRequest<QueryExportAgentPlayerReportingsV2Args> {
        const filter = this.mapToFilterObject(downloadRequestPayload.filter);
        const queryArgs = this.getGqlPageArgs(filter);
        return {
            query: this.buildExportQuery(),
            variables: {
                report_name: downloadRequestPayload.report_name,
                filter: queryArgs?.filter,
                sort: queryArgs?.sort,
                start: 0,
                end: maxServerDownloadPageSize,
                fields: downloadRequestPayload.columns.map(column => ({
                    column_name: column.name,
                    field: column.field,
                })),
            },
        };
    }

    protected validateFilters(_filter: Partial<Record<PlayerLevelReportV2QueryFilterKeys, unknown>>): void {
        return;
    }
    protected buildFilter(filter: Filter<PlayerLevelReportV2QueryFilterKeys>): {filter: AgentPlayerReportingFilter} {
        const referrer_player_id = this.toGQLStringFilter(filter, 'referrer_id');
        return {
            filter: {
                text: this.getGQLTextFilter(
                    Object.keys(this.filterFieldsMapper).map((key: PlayerLevelReportV2ServerTextFilterKeys) =>
                        this.toGQLTextFilter(this.filterFieldsMapper[key], filter[key] as string)
                    )
                ),
                referrer: referrer_player_id
                    ? {
                          referrer_player_id,
                          is_downstream: this.toGQLBooleanFilter(filter, 'is_downstream'),
                          is_agent: false,
                      }
                    : undefined,
                transaction_started_ts: this.toGQLDateRange(
                    this.getLatestTimestamp(filter.started_at_from as number, filter.started_at_min as number),
                    filter.started_at_to
                ),
            },
        };
    }

    protected buildQuery(fields: PlayerLevelReportV2QueryKeys[]): DocumentNode {
        const requiredFields: PlayerLevelReportV2QueryKeys[] = ['items.uid', 'total_count'];
        const queryFields: PlayerLevelReportV2QueryKeys[] = [...new Set([...fields, ...requiredFields])];
        return gql`
            query GetAgentPlayerReportingsV2($filter: AgentPlayerReportingFilter, $sort: Sorting, $start: Int, $end: Int) {
                getAgentPlayerReportingsV2(filter: $filter, sort: $sort, end: $end, start: $start) {
                    ${this.buildQueryFields(queryFields)}
                }
            }
        `;
    }

    private readonly filterFieldsMapper: Record<PlayerLevelReportV2ServerTextFilterKeys, (keyof AgentPlayerReportingV2)[]> = {
        country: ['iso_alpha2_country_code'],
        uid: ['uid'],
        uid_un: ['uid', 'username'],
        username: ['username'],
    };

    protected buildSort(filter: Filter): {sort?: Sorting | Sorting[]} {
        return filter.sortOrder || filter.sortField
            ? {
                  sort: {
                      sort: this.mapSortingFields(filter.sortField as PlayerLevelReportV2SortKeys),
                      order: [SortOrder.Asc, 'asc'].includes(filter.sortOrder as string) ? SortOrder.Asc : SortOrder.Desc,
                  },
              }
            : {};
    }

    private mapSortingFields(key: PlayerLevelReportV2SortKeys): string {
        const mapping: Partial<Record<PlayerLevelReportV2SortKeys, string>> = {
            global_spins_ggr: 'global_spins_expected_ggr',
        };
        return mapping[key] ? mapping[key] : key;
    }

    private buildExportQuery() {
        return gql`
            query ExportAgentPlayerReportingsV2(
                $filter: AgentPlayerReportingFilter
                $report_name: String!
                $fields: [ReportField]
                $sort: Sorting
                $start: Int
                $end: Int
            ) {
                exportAgentPlayerReportingsV2(
                    filter: $filter
                    report_name: $report_name
                    fields: $fields
                    sort: $sort
                    end: $end
                    start: $start
                ) {
                    url
                }
            }
        `;
    }
}
